import { useState } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, Clock, RefreshCcw, ShieldCheck, Star } from 'lucide-react'

import { ShareButton } from '@/components/share'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { tierLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import { useOrderService, useService, useServiceReviews } from '@/features/services/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatRupiah } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { ServicePackage } from '@/lib/types'

const route = getRouteApi('/public/jasa/$slug')

export function ServiceDetailPage() {
  const { slug } = route.useParams()
  const { data: service, isLoading } = useService(slug)
  const { data: reviews } = useServiceReviews(slug)
  const { data: session } = useSession()
  const navigate = useNavigate()

  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [orderTarget, setOrderTarget] = useState<ServicePackage | null>(null)

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    )
  }

  if (!service) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Jasa tidak ditemukan"
          description="Etalase mungkin sedang dijeda penjualnya."
          action={
            <Link to="/jasa">
              <Button variant="outline">Kembali ke daftar jasa</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const activeTier = selectedTier ?? service.packages[0]?.tier ?? null
  const activePackage = service.packages.find((pkg) => pkg.tier === activeTier) ?? service.packages[0]
  const isOwner = session?.user.id === service.seller.id

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
      <Link
        to="/jasa"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Paket jasa
      </Link>

      <div className="mt-4 grid gap-8 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <div>
            <Badge tone="brand">{service.category}</Badge>
            <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">{service.title}</h1>

            <div className="mt-4">
              <ShareButton title={service.title} text={`Jasa di skava.id: ${service.title}`} />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted">
              <Link
                to="/talenta/$expertId"
                params={{ expertId: service.seller.id }}
                className="font-semibold text-ink hover:text-brand"
              >
                {service.seller.full_name}
              </Link>
              {service.rating_count > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Star className="size-4 fill-amber-400 text-amber-400" />
                  {service.rating.toFixed(1)} dari {service.rating_count} ulasan
                </span>
              ) : (
                <span>Belum ada ulasan</span>
              )}
              <span>{service.order_count} pesanan selesai</span>
            </div>
          </div>

          {service.thumbnail_url ? (
            <img
              src={service.thumbnail_url}
              alt=""
              className="aspect-[16/9] w-full rounded-2xl border border-line object-cover"
            />
          ) : null}

          <Card>
            <CardHeader title="Tentang jasa ini" />
            <CardBody>
              <p className="whitespace-pre-line leading-relaxed text-muted">{service.description}</p>

              {service.skills.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {service.skills.map((skill) => (
                    <span key={skill} className="rounded-full bg-sunken px-3 py-1 text-sm font-medium text-muted">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Ulasan pembeli" />
            <CardBody>
              {!reviews || reviews.length === 0 ? (
                <p className="text-sm text-muted">
                  Belum ada ulasan. Ulasan hanya dapat ditulis pembeli setelah pekerjaan benar-benar selesai.
                </p>
              ) : (
                <ul className="space-y-3">
                  {reviews.map((review) => (
                    <li key={review.id} className="rounded-xl border border-line p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-semibold text-ink">{review.author_name}</p>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-ink">
                          <Star className="size-4 fill-amber-400 text-amber-400" />
                          {review.rating}
                        </span>
                      </div>
                      {review.comment ? <p className="mt-2 text-sm text-muted">{review.comment}</p> : null}
                      <p className="mt-2 text-xs text-subtle">{formatDate(review.created_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <aside className="lg:sticky lg:top-24 lg:h-fit">
          <Card className="overflow-hidden">
            {/* Pemilihan paket memakai tab: perbandingan bertingkat lebih
                mudah dibaca daripada tiga kartu berjajar pada layar sempit. */}
            <div className="flex border-b border-line">
              {service.packages.map((pkg) => (
                <button
                  key={pkg.tier}
                  type="button"
                  onClick={() => setSelectedTier(pkg.tier)}
                  className={cn(
                    'flex-1 px-3 py-3 text-sm font-bold transition',
                    pkg.tier === activeTier
                      ? 'border-b-2 border-brand text-brand-ink'
                      : 'text-muted hover:text-ink',
                  )}
                >
                  {tierLabel[pkg.tier] ?? pkg.tier}
                </button>
              ))}
            </div>

            {activePackage ? (
              <CardBody className="space-y-4">
                <div>
                  <p className="text-sm font-bold text-ink">{activePackage.title}</p>
                  {activePackage.description ? (
                    <p className="mt-1 text-sm text-muted">{activePackage.description}</p>
                  ) : null}
                </div>

                <p className="text-3xl font-extrabold tracking-tight text-ink">
                  {formatRupiah(activePackage.price)}
                </p>

                <div className="flex flex-wrap gap-4 text-sm text-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="size-4" />
                    {activePackage.delivery_days} hari pengerjaan
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <RefreshCcw className="size-4" />
                    {activePackage.revisions} kali revisi
                  </span>
                </div>

                {activePackage.features.length > 0 ? (
                  <ul className="space-y-2 border-t border-line pt-4 text-sm text-muted">
                    {activePackage.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                ) : null}

                {isOwner ? (
                  <Link to="/app/jasa">
                    <Button fullWidth variant="outline">
                      Kelola jasa ini
                    </Button>
                  </Link>
                ) : (
                  <Button
                    fullWidth
                    size="lg"
                    onClick={() => {
                      if (!session) {
                        navigate({ to: '/masuk', search: { redirect: `/jasa/${slug}` } })
                        return
                      }
                      setOrderTarget(activePackage)
                    }}
                  >
                    Pesan paket ini
                  </Button>
                )}

                <div className="flex items-start gap-2.5 rounded-xl bg-sunken p-3.5 text-xs leading-relaxed text-muted">
                  <ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand" />
                  <p>
                    Pemesanan menerbitkan kontrak escrow. Dana Anda dikunci setelah disetor dan baru dilepas ke
                    penjual saat hasil kerja Anda setujui.
                  </p>
                </div>
              </CardBody>
            ) : (
              <CardBody>
                <p className="text-sm text-muted">Penjual belum menyiapkan paket pada jasa ini.</p>
              </CardBody>
            )}
          </Card>
        </aside>
      </div>

      <OrderDialog target={orderTarget} onClose={() => setOrderTarget(null)} />
    </div>
  )
}

function OrderDialog({ target, onClose }: { target: ServicePackage | null; onClose: () => void }) {
  const orderService = useOrderService()
  const toast = useToast()
  const navigate = useNavigate()
  const [requirements, setRequirements] = useState('')

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title="Pesan paket jasa"
      description={target ? `${target.title} · ${formatRupiah(target.price)}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={orderService.isPending}
            disabled={requirements.trim().length < 20}
            onClick={() => {
              if (!target) return
              orderService.mutate(
                { packageId: target.id, requirements },
                {
                  onSuccess: (data) => {
                    toast.success('Pesanan dibuat', 'Lanjutkan dengan menyetor dana escrow.')
                    setRequirements('')
                    onClose()
                    navigate({ to: '/app/kontrak/$contractId', params: { contractId: data.order.contract_id } })
                  },
                  onError: (error) =>
                    toast.error('Gagal memesan', error instanceof ApiError ? error.message : undefined),
                },
              )
            }}
          >
            Buat pesanan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Kebutuhan Anda"
          rows={6}
          value={requirements}
          onChange={(event) => setRequirements(event.target.value)}
          placeholder="Jelaskan konteks pekerjaan, data atau akses yang tersedia, dan hasil akhir yang Anda harapkan."
          hint="Minimal 20 karakter. Makin jelas, makin kecil kemungkinan revisi berulang."
        />

        {target ? (
          <div className="rounded-xl bg-sunken p-4">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">Nilai pesanan</dt>
                <dd className="font-semibold text-ink">{formatRupiah(target.price)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Lama pengerjaan</dt>
                <dd className="font-semibold text-ink">{target.delivery_days} hari</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Jatah revisi</dt>
                <dd className="font-semibold text-ink">{target.revisions} kali</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs leading-relaxed text-subtle">
              Setelah pesanan dibuat, Anda akan diarahkan ke kontraknya untuk menyetor dana escrow. Pekerjaan
              dimulai setelah dana benar-benar terkunci.
            </p>
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
